import { useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { useFinancialWarnings } from 'contexts/FinancialWarningsContext';
import { debounce } from 'lodash';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { initialPeriodsTableRow, IPeriodsTableRow } from 'components/BusinessToolForm/constants/periods';
import { applyExcessiveProceedsForTotal } from 'components/BusinessToolForm/utils/financialCalculations';
import { PERIODS_OPERATION_TYPES, summarizeSubtractPeriodValues } from 'components/BusinessToolForm/utils/helpers';

import { observedFields } from '../../utils/form';

export const useGetInvestingActivitiesNetCashCalculation = () => {
  const { setValue } = useFormContext();
  const { excessiveProceedsFromSalePeriods } = useFinancialWarnings();
  const excessiveProceedsRef = useRef(excessiveProceedsFromSalePeriods);
  excessiveProceedsRef.current = excessiveProceedsFromSalePeriods;

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  const [investingActivitiesNetCashFiled] = observedFields.investingActivitiesNetCash.setCalculatedValueFieldNames;

  const getInvestingActivitiesNetCashCalculationHandler = useMemo(
    () =>
      debounce((content) => {
        if (!canAccess) return;

        const [purchasePPE, investingActivitiesTableValues] = content;
        const rawProceeds = (investingActivitiesTableValues[2] ?? initialPeriodsTableRow) as unknown as IPeriodsTableRow;
        const proceedsForTotal = applyExcessiveProceedsForTotal(rawProceeds, excessiveProceedsRef.current);

        const tableWithAdjustedProceeds = investingActivitiesTableValues.map(
          (row: IPeriodsTableRow, index: number) => (index === 2 ? proceedsForTotal : row),
        );

        const investingActivitiesNetCash = summarizeSubtractPeriodValues(
          [...tableWithAdjustedProceeds, purchasePPE],
          PERIODS_OPERATION_TYPES.ADD,
        );

        setValue(investingActivitiesNetCashFiled, investingActivitiesNetCash);
      }, 500),

    [canAccess, investingActivitiesNetCashFiled, setValue],
  );

  return getInvestingActivitiesNetCashCalculationHandler;
};